import type { EventBus } from "../core/EventBus.ts";
import type { AppEventMap } from "../core/EventBus.ts";
import type {
  Achievement,
  AchievementId,
  ThreatLevelId,
} from "../core/MetaState.ts";

/** Threshold: max combo in a single run required for combo_master. */
export const COMBO_MASTER_THRESHOLD = 20;
/** Threshold: total runs for veteran achievement. */
export const VETERAN_THRESHOLD = 7;
/** Threshold: total cards drafted for card_collector. */
export const CARD_COLLECTOR_THRESHOLD = 35;
/** Threshold: unique skills used for skill_seeker. */
export const SKILL_SEEKER_THRESHOLD = 12;
/** Threshold: minimum wave reached for the near_death flag (unlocks the
 *  Failsafe Protocol module). Shipped + test-locked at 11; GDD_V2.md's "Wave
 *  14+" was stale prose and was reconciled to this live value (BL-263). */
export const NEAR_DEATH_MIN_WAVE = 11;

/**
 * Run-scoped achievement tracker. Listens to game events and accumulates
 * progress. At run end, returns pending unlocks without mutating MetaState.
 * D-040: Forward-only tracking, counter-based (veteran, card_collector, skill_seeker)
 * vs flag-based (all others).
 */

export interface RunStats {
  waveReached: number;
  score: number;
  maxCombo: number;
  leaks: number;
  cardsSelected: number;
  uniqueSkillsUsed: Set<string>;
  bossKilledClean: boolean;
  livesAtEnd: number;
}

export interface PendingUnlock {
  id: AchievementId;
  progressDelta: number;
}

export class AchievementTracker {
  private bus: EventBus;
  private unsubscribers: (() => void)[] = [];
  private stats: RunStats;

  constructor(bus: EventBus) {
    this.bus = bus;
    this.stats = this.createFreshStats();
    this.subscribe();
  }

  /** Current accumulated run stats (read-only snapshot). */
  getStats(): Readonly<RunStats> {
    return this.stats;
  }

  /**
   * Evaluate achievements at run end. Returns pending unlocks.
   * Pure evaluation — does NOT mutate the given achievements array.
   */
  evaluate(
    achievements: readonly Achievement[],
    cumulativeRuns: number,
    cumulativeCards: number,
    cumulativeUniqueSkills: Set<string>,
    livesAtEnd?: number,
    /**
     * Chain step A (2026-08-18) — the run-end facts `hardcore` judges:
     * decided by the owner as "FALLOUT durchgespielt": `won` (wave 35 held —
     * or Echo Mode, which by definition held it first) on `threatLevel`
     * "fallout". NO extra conditions (no no-leak clause — leaks already price
     * themselves via the lives bonus; no operator restriction, no revive
     * rule — none exists). Optional so pre-contract callers stay valid; the
     * run-end handler passes it from `RunEndStats`.
     */
    runEnd?: { won: boolean; threatLevel: ThreatLevelId }
  ): PendingUnlock[] {
    const pending: PendingUnlock[] = [];
    const effectiveLives = livesAtEnd ?? this.stats.livesAtEnd;

    for (const ach of achievements) {
      if (ach.unlocked) continue;

      switch (ach.id) {
        case "veteran":
          // Counter-based: total runs completed (incremented externally)
          if (cumulativeRuns + 1 >= ach.target) {
            pending.push({ id: "veteran", progressDelta: 1 });
          }
          break;

        case "card_collector":
          // Counter-based: total cards drafted
          if (cumulativeCards + this.stats.cardsSelected >= ach.target) {
            pending.push({
              id: "card_collector",
              progressDelta: this.stats.cardsSelected,
            });
          }
          break;

        case "combo_master":
          // Flag: max combo >= 20 in single run
          if (this.stats.maxCombo >= COMBO_MASTER_THRESHOLD) {
            pending.push({ id: "combo_master", progressDelta: 1 });
          }
          break;

        case "near_death":
          // Flag: reached the near-death wave floor and ended at 0 lives.
          if (
            this.stats.waveReached >= NEAR_DEATH_MIN_WAVE &&
            effectiveLives <= 0
          ) {
            pending.push({ id: "near_death", progressDelta: 1 });
          }
          break;

        case "first_contact":
          // Flag: epoch I complete (wave 12 reached)
          if (this.stats.waveReached >= 12) {
            pending.push({ id: "first_contact", progressDelta: 1 });
          }
          break;

        case "clean_sweep":
          // Flag: epoch I complete + leaks <= 2
          if (this.stats.waveReached >= 12 && this.stats.leaks <= 2) {
            pending.push({ id: "clean_sweep", progressDelta: 1 });
          }
          break;

        case "skill_seeker": {
          // Counter-based: unique skills used across runs
          const totalUnique = new Set([
            ...cumulativeUniqueSkills,
            ...this.stats.uniqueSkillsUsed,
          ]);
          if (totalUnique.size >= ach.target) {
            pending.push({
              id: "skill_seeker",
              progressDelta: this.stats.uniqueSkillsUsed.size,
            });
          }
          break;
        }

        case "perfect_defense":
          // Flag: boss killed + 0 leaks in that wave
          if (this.stats.bossKilledClean) {
            pending.push({ id: "perfect_defense", progressDelta: 1 });
          }
          break;

        case "hardcore":
          // Owner decision 2026-08-17/18 (decision-hardcore-achievement.md):
          // hardcore = a FALLOUT run that WON (wave 35 held). Nothing else —
          // a fallout victory WITH leaks unlocks it, any other level's
          // victory does not, a lost/aborted fallout run does not.
          if (runEnd?.won && runEnd.threatLevel === "fallout") {
            pending.push({ id: "hardcore", progressDelta: 1 });
          }
          break;
      }
    }

    return pending;
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }

  private createFreshStats(): RunStats {
    return {
      waveReached: 0,
      score: 0,
      maxCombo: 0,
      leaks: 0,
      cardsSelected: 0,
      uniqueSkillsUsed: new Set(),
      bossKilledClean: false,
      livesAtEnd: Infinity,
    };
  }

  private subscribe(): void {
    this.on("wave.completed", (d: unknown) => {
      const ev = d as { wave: number };
      if (ev.wave > this.stats.waveReached) {
        this.stats.waveReached = ev.wave;
      }
    });

    this.on("score.combo", (d: unknown) => {
      const ev = d as { combo: number };
      if (ev.combo > this.stats.maxCombo) {
        this.stats.maxCombo = ev.combo;
      }
    });

    this.on("enemy.reachedEnd", () => {
      this.stats.leaks++;
    });

    this.on("draft.cardSelected", (d: unknown) => {
      const ev = d as { card: { id: string } };
      this.stats.cardsSelected++;
      this.stats.uniqueSkillsUsed.add(ev.card.id);
    });

    this.on("game.over", (d: unknown) => {
      const ev = d as { score: number; wave: number };
      this.stats.score = ev.score;
      this.stats.waveReached = ev.wave;
    });

    this.on("boss.killed", (d: unknown) => {
      const ev = d as { leaksThisWave?: number };
      if ((ev.leaksThisWave ?? 0) === 0) {
        this.stats.bossKilledClean = true;
      }
    });
  }

  private on(event: keyof AppEventMap, handler: (data: unknown) => void): void {
    this.unsubscribers.push(this.bus.on(event, handler));
  }
}
